import { createApp, ref, reactive, computed, nextTick, watch, onMounted, onUnmounted, toRef, useTemplateRef, markRaw } from 'vue';
import { createI18n, useI18n } from 'vue-i18n';
import WzRecorder from "./audio/wzrecorder";
import * as THREE from 'three';
import {GLTFLoader} from 'three/examples/jsm/loaders/GLTFLoader.js';
import {lerp} from 'three/src/math/MathUtils.js';
import enTranslations from './translations/en';
import faTranslations from './translations/fa';
import kuTranslations from './translations/ku';
import ukTranslations from './translations/uk';
import arTranslations from './translations/ar';

const translations = {
    en: enTranslations,
    fa: faTranslations,
    ku: kuTranslations,
    uk: ukTranslations,
    ar: arTranslations
};

// Cleans assistant responses by removing 【...】 segments, collapsing extra spaces, trimming ends
function stripBracketedClean(text) {
    if (typeof text !== 'string') return text;
    return text
        .replace(/【[^】]*】/g, '') // remove all 【...】 blocks
        .replace(/\s{2,}/g, ' ') // collapse multiple whitespace
        .trim();
}

function debug() {
    if(__yuki.env.isDev) {
        console.log.apply(null, arguments);
    }
}

const maxRecordingDurationSeconds = 30;

// Get constants from backend
const Emotions = Object.freeze(__yuki.constants.emotions);
const ColorSchemes = Object.freeze(__yuki.constants.colorSchemes);
const Views = Object.freeze(__yuki.constants.views);
const Modes = Object.freeze(__yuki.constants.modes);
const Languages = Object.freeze(__yuki.constants.languages);

const i18n = createI18n({
    legacy: false,
    locale: 'en',
    fallbackLocale: 'en',
    missingWarn: __yuki.env.isDev,
    fallbackWarn: __yuki.env.isDev,
    warnHtmlMessage: __yuki.env.isDev,
    runtimeOnly: false,
    escapeParameter: false,
    messages: translations
});

const app = createApp({
    template: '#yuki-template',
    setup() {
        const { t } = useI18n();
        const availableLanguages = Languages;
        const availableModes = Modes;
        const availableViews = Views;
        const availableColorSchemes = ColorSchemes;

        let chatMessagesWindow = useTemplateRef('chat-messages');
        let disclaimerText = useTemplateRef('disclaimer-text');

        // Links configuration
        const links = {
            ourSources: {
                href: 'https://yu-ki.org/2025/01/22/die-yuki-app/',
                target: '_blank'
            },
            moreAboutAi: {
                href: 'https://yu-ki.org',
                target: '_blank'
            },
            aboutUs: {
                href: 'https://www.kubusev.org/ueber-uns/',
                target: '_blank'
            },
            legalNotice: {
                href: '/legal-notice',
                target: '_blank'
            }
        };

        // Track active requests
        const activeRequests = reactive({
            controllers: new Map(),
            add(key) {
                if (this.controllers.has(key)) {
                    this.controllers.get(key).abort();
                }
                const controller = new AbortController();
                this.controllers.set(key, controller);
                return controller.signal;
            },
            abort(key) {
                if (this.controllers.has(key)) {
                    this.controllers.get(key).abort();
                    this.controllers.delete(key);
                }
            },
            abortAll() {
                this.controllers.forEach(controller => controller.abort());
                this.controllers.clear();
            }
        });

        let state = ref({
            loading: true,
            emotion: Emotions.HAPPY,
            mode: null,
            language: Languages.ENGLISH,
            view: null,
            hasReadDisclaimer: false,
            tutorialAvailable: false,
        });

        let chat = reactive({
            generating: false,
            messages: [],
            textInput: '',
            discardRecordingUponStop: false,
            isRecording: false,
            recorder: null,
            recordingDuration: `0s/${maxRecordingDurationSeconds}s`,
            currentAudioData: null,
            showColorPicker: false,
            showSettingsOverlay: false
        });

        const chatbotClasses = computed(() => ({
            loading: state.value.loading,
            [`scheme-${state.value.view.colorScheme}`]: true,
            [`view-${state.value.view.name}`]: true,
        }));

        const viewClass = ref(null);

        const avatarRef = ref(null);

        changeToMode(Modes.SMALLTALK);
        changeToView(Views.LOGOS);

        function cleanupAvatarAudio() {
            if (avatarRef.value?.cleanupAudio) {
                avatarRef.value.cleanupAudio();
            }
        }

        function getAnimateDuration() {
            return parseInt(getComputedStyle(document.documentElement).getPropertyValue('--animate-duration')) || 500;
        }

        function leaveView(view, done = () => {}) {
            debug(`leaving view ${view ? view.name : 'initial'}`);

            if(!view) {
                done();
                return;
            }

            let delay = 0;
            let defaultAnimationDelay = getAnimateDuration();
            resetCurrentAudioData();
            cleanupAvatarAudio();
            //resetEmotion();
            chat.showColorPicker = false;
            chat.showSettingsOverlay = false;

            setViewClass(null);

            switch(view.name) {
                case Views.LOGOS.name:
                    setViewClass('animate__animated animate__fadeOut');
                    delay = defaultAnimationDelay;
                    break;
                case Views.SPLASHSCREEN.name:
                    setViewClass('animate__animated animate__bounceOutLeft');
                    delay = defaultAnimationDelay;
                    break;
                case Views.DISCLAIMER.name:
                    setViewClass('animate__animated animate__bounceOutLeft');
                    delay = defaultAnimationDelay;
                    break;
                case Views.LANGUAGE_SELECTION.name:
                    setViewClass('animate__animated animate__fadeOut');
                    delay = defaultAnimationDelay;
                    break;
                case Views.WELCOME.name:
                    activeRequests.abort('welcome-text');
                    setViewClass('animate__animated animate__fadeOut');
                    delay = defaultAnimationDelay;
                    break;
                case Views.MODE_SELECTION.name:
                    setViewClass('animate__animated animate__fadeOut');
                    delay = defaultAnimationDelay;
                    break;
                case Views.CHAT.name:
                    setViewClass('animate__animated animate__fadeOut');
                    delay = defaultAnimationDelay;
                    break;
            }

            if(delay > 0) {
                setTimeout(done, delay);
            } else {
                done();
            }
        }

        function enterView(view) {
            debug(`entering view ${view ? view.name : 'initial'}`);

            setViewClass(null);

            let delay = 0;
            let defaultAnimationDelay = getAnimateDuration();
            let done = () => {};

            switch(view.name) {
                case Views.LOGOS.name:
                    setViewClass('animate__animated animate__fadeIn');
                    delay = defaultAnimationDelay;
                    break;
                case Views.SPLASHSCREEN.name:
                    setViewClass('animate__animated animate__bounceInDown');
                    delay = defaultAnimationDelay;
                    break;
                case Views.DISCLAIMER.name:
                    setViewClass('animate__animated animate__bounceInRight');
                    delay = defaultAnimationDelay;
                    done = () => {
                        triggerScrollOnDisclaimerText();
                    };
                    break;
                case Views.LANGUAGE_SELECTION.name:
                    setViewClass('animate__animated animate__bounceInRight');
                    delay = defaultAnimationDelay;
                    break;
                case Views.WELCOME.name:
                    setViewClass('animate__animated animate__bounceInUp');
                    delay = defaultAnimationDelay;
                    done = () => {
                        readWelcomeText();
                    };
                    break;
                case Views.MODE_SELECTION.name:
                    setViewClass('animate__animated animate__fadeIn');
                    delay = defaultAnimationDelay;
                    break;
                case Views.CHAT.name:
                    setViewClass('animate__animated animate__fadeIn');
                    delay = defaultAnimationDelay;
                    done = () => {
                        scrollToBottomOfChat();
                    };
                    break;
            }

            if(delay > 0) {
                setTimeout(done, delay);
            } else {
                done();
            }
        }

        function leaveMode(mode) {
            debug(`leaving mode ${mode ? mode : 'initial'}`);

            if(!mode) {
                return;
            }

            switch(mode) {
                case Modes.SMALLTALK:
                    break;
                case Modes.SUPPORT:
                    break;
            }
        }

        function enterMode(mode) {
            debug(`entering mode ${mode ? mode : 'initial'}`);

            switch(mode) {
                case Modes.SMALLTALK:
                    resetChatMessages();
                    break;
                case Modes.SUPPORT:
                    resetChatMessages();
                    break;
            }
        }

        function changeToView(newView, storePreviousView = false) {
            if(state.value.previousView) {
                debug(`wanted view ${newView.name}, but restoring previous view ${state.value.previousView.name}`)
                newView = state.value.previousView;
                setPreviousView(null);
                storePreviousView = false;
            }

            if(state.value.view !== null && state.value.view.name === newView.name) {
                return;
            }

            leaveView(state.value.view, () => {
                navigateToView(newView, storePreviousView, () => {
                   enterView(newView);
                });
            });
        }

        function navigateToView(newView, storePreviousView = false, done = () => {}) {
            if(storePreviousView) {
                setPreviousView(state.value.view);
            }

            setView(newView);

            nextTick(() => {
                done();
            })
        }

        function setPreviousView(previousView = null) {
            state.value.previousView = previousView;
        }

        function setView(newView) {
            state.value.view = newView;
        }

        function changeToMode(newMode) {
            if(state.value.mode !== null && state.value.mode === newMode) {
                return;
            }

            leaveMode(state.value.mode);
            navigateToMode(newMode);
            enterMode(newMode);
        }

        function navigateToMode(newMode) {
            setMode(newMode);
        }

        function setMode(newMode) {
            state.value.mode = newMode;
        }

        function tapToStart() {
            changeToView(Views.DISCLAIMER);

        }

        function acceptDisclaimer() {
            changeToView(Views.LANGUAGE_SELECTION);
        }

        function changeNativeLanguage() {
            changeToView(Views.LANGUAGE_SELECTION, true);
        }

        function next() {
            changeToView(Views.WELCOME);
        }

        function letsStart() {
            changeToView(Views.MODE_SELECTION);
        }

        function selectModeSmalltalk() {
            changeToMode(Modes.SMALLTALK);
            initializeChat();
            changeToView(Views.CHAT);
        }

        function selectModeSupport() {
            changeToMode(Modes.SUPPORT);
            initializeChat();
            changeToView(Views.CHAT);
        }

        function startTutorial(restart = false) {
            //TODO: tutorial functionality
            debug('starting tutorial', restart ? 'restart' : 'start');
        }

        function setViewClass(value) {
            viewClass.value = value;
        }

        function readWelcomeText() {
            nextTick(() => {
                const data = new FormData();
                data.append('action', 'text_to_speech');
                data.append('language', state.value.language.name);

                const readText = t('chatbot.welcome.text')
                    .replaceAll('<span class="color-light">', '')
                    .replaceAll('</span>', '');

                data.append('text', readText);

                const signal = activeRequests.add('welcome-text');

                fetch(__yuki.backend.url, {
                    method: "POST",
                    body: data,
                    signal: signal
                })
                    .then((response) => {
                        // Check if request was aborted
                        if (signal.aborted) {
                            throw new Error('AbortError');
                        }
                        return response.json();
                    })
                    .then((data) => {
                        debug('text to speech response', data);

                        // Check if request was aborted before playing audio
                        if (signal.aborted) {
                            throw new Error('AbortError');
                        }

                        if(data.success && data.audio_data) {
                            setEmotion(Emotions.HAPPY);
                            setCurrentAudioData(data.audio_data);
                        } else {
                            console.error('Failed to get audio data');
                        }
                    })
                    .catch((error) => {
                        if (error.name !== 'AbortError') {
                            console.error('Failed to get audio:', error);
                        }
                    });
            })
        }

        function triggerScrollOnDisclaimerText() {
            // First check - immediate after nextTick
            nextTick(() => {
                if(disclaimerText.value) {
                    disclaimerText.value.dispatchEvent(new Event('scroll'));
                    
                    // Second check - after short delay (50ms)
                    setTimeout(() => {
                        if(disclaimerText.value) {
                            disclaimerText.value.dispatchEvent(new Event('scroll'));
                            
                            // Third check - after longer delay (500ms) to ensure content is fully rendered
                            setTimeout(() => {
                                if(disclaimerText.value) {
                                    disclaimerText.value.dispatchEvent(new Event('scroll'));
                                }
                            }, 500);
                        }
                    }, 50);
                }
            });
        }

        function scrollToBottomOfChat() {
            nextTick(() => {
                if(chatMessagesWindow.value) {
                    chatMessagesWindow.value.scrollTop = chatMessagesWindow.value.scrollHeight;
                }
            })
        }

        function checkHasReadDisclaimer(event) {
            if (state.value.hasReadDisclaimer) {
                return true;
            }

            const target = event.target;
            const scrollTop = target.scrollTop;
            const scrollHeight = target.scrollHeight;
            const clientHeight = target.clientHeight;
            
            // Calculate how far we've scrolled as a percentage (0-1)
            const scrollPercentage = scrollTop / (scrollHeight - clientHeight);
            
            // Three different ways to check if user has read the disclaimer
            const scrollDifference = Math.abs(scrollTop - (scrollHeight - clientHeight));
            const isAtBottom = scrollDifference <= 5; // Increased tolerance from 2 to 5
            const isCloseToBottom = scrollPercentage >= 0.95; // 95% scrolled is close enough
            const noScrollNeeded = scrollHeight <= clientHeight;
            
            state.value.hasReadDisclaimer = isAtBottom || isCloseToBottom || noScrollNeeded;

            console.debug('Scroll check:', { 
                scrollTop, scrollHeight, clientHeight, 
                scrollPercentage, isAtBottom, isCloseToBottom, noScrollNeeded,
                hasRead: state.value.hasReadDisclaimer 
            });

            return state.value.hasReadDisclaimer;
        }   

        function selectLanguage(language) {
            if (language.disabled) {
                return;
            }

            debug(`switching language from ${state.value.language.name} to ${language.name}`);

            state.value.language = language;
            i18n.global.locale.value = language.id;
        }

        function goToModeSelect() {
            changeToView(Views.MODE_SELECTION, true);
        }

        function startRecording() {
            chat.isRecording = true;

            chat.recorder = new WzRecorder({
                onRecordingStop: function(blob) {
                    chat.isRecording = false;

                    if(!chat.discardRecordingUponStop) {
                        addAudioMessageTranscription(blob);
                    }
                },
                onRecording: function(milliseconds) {
                    const seconds = Math.floor(milliseconds / 1000);
                    let durationString = "";

                    if (seconds < maxRecordingDurationSeconds) {
                        durationString = `${seconds}s/${maxRecordingDurationSeconds}s`;
                    } else {
                        durationString = `${maxRecordingDurationSeconds}s/${maxRecordingDurationSeconds}s`;
                    }

                    chat.recordingDuration = durationString;

                    // Stop recording if max duration is reached
                    if (seconds >= maxRecordingDurationSeconds) {
                        stopRecording(false); // Don't discard the recording
                    }
                }
            });

            chat.recordingDuration = `0s/${maxRecordingDurationSeconds}s`;
            chat.recorder.start();
        }

        function stopRecording(discardRecordingUponStop = false) {
            chat.discardRecordingUponStop = discardRecordingUponStop;
            chat.recorder.stop();
        }
        function addAudioMessageTranscription(blob) {
            chat.generating = true;

            const textMessageIndex = appendChatMessage({
                role: "user",
                content: t('chatbot.chat.message.transcribing'),
                contentTranslated: "",
                translationAllowed: false,
                translationActive: false,
                isTranscribed: true,
                error: false,
                serverId: null,
                audioData: null,
                emotionalReaction: null
            });

            var data = new FormData();
            data.append('action', 'transcribe_audio');
            data.append('language', state.value.language.name);
            data.append("audio", blob, 'recording.wav');
            data.append('mode', state.value.mode);

            const signal = activeRequests.add('transcribe-audio');

            fetch(__yuki.backend.url, {
                method: "POST",
                body: data,
                signal: signal
            })
                .then((response) => {
                    // Check if request was aborted
                    if (signal.aborted) {
                        throw new Error('AbortError');
                    }
                    return response.json();
                })
                .then((data) => {
                    debug('audio msg response', data);

                    // Check if request was aborted before processing
                    if (signal.aborted) {
                        throw new Error('AbortError');
                    }

                    if(data.success) {
                        if(data.input.message_id) {
                            chat.messages[textMessageIndex].serverId = data.input.message_id;
                        }

                        chat.messages[textMessageIndex].content = data.input.message;

                        if(data.output.message) {
                            appendChatMessage({
                                role: "assistant",
                                content: data.output.message,
                                contentTranslated: "",
                                translationAllowed: true,
                                translationActive: false,
                                isTranscribed: false,
                                error: false,
                                serverId: data.output.message_id,
                                audioData: data.output.audio_data,
                                emotionalReaction: data.output.emotional_reaction,
                            });
                        } else {
                            console.error('no answer from assistant');
                            chat.messages[textMessageIndex].error = true;
                        }
                    } else {
                        console.error(data.error);
                        chat.messages[textMessageIndex].content = t('chatbot.chat.message.transcribing.error');
                        chat.messages[textMessageIndex].error = true;
                    }

                    chat.generating = false;
                }).catch((error) => {
                if (error.name !== 'AbortError') {
                    console.error(error);
                    chat.messages[textMessageIndex].content = t('chatbot.chat.message.transcribing.error');
                    chat.messages[textMessageIndex].error = true;
                }
                chat.generating = false;
            });
        }

        function resetChatMessages() {
            chat.messages = [];
        }

        function initializeChat() {
            const data = new FormData();
            data.append('action', 'initialize_chat');
            data.append('language', state.value.language.name);
            data.append('mode', state.value.mode);

            chat.generating = true;

            const signal = activeRequests.add('initialize-chat');

            fetch(__yuki.backend.url, {
                method: "POST",
                body: data,
                signal: signal
            })
                .then((response) => {
                    // Check if request was aborted
                    if (signal.aborted) {
                        throw new Error('AbortError');
                    }
                    return response.json();
                }).then((data) => {
                debug('initialize chat response', data);

                // Check if request was aborted before processing
                if (signal.aborted) {
                    throw new Error('AbortError');
                }

                if(data.messages && Array.isArray(data.messages)) {
                    data.messages.forEach(message => {
                        appendChatMessage({
                            role: message.role,
                            content: message.content,
                            contentTranslated: "",
                            translationAllowed: message.role === "assistant",
                            translationActive: false,
                            isTranscribed: false,
                            error: false,
                            serverId: message.id,
                            audioData: null,
                            emotionalReaction: null
                        });
                    });
                }

                if(data.audio_data) {
                    setCurrentAudioData(data.audio_data);
                }

                chat.generating = false;
            }).catch((error) => {
                if (error.name !== 'AbortError') {
                    console.error('Failed to initialize chat:', error);
                }
                chat.generating = false;
            });
        }

        function appendChatMessage(message) {
            // Sanitize assistant content before storing
            if (message.role === 'assistant' && typeof message.content === 'string') {
                message.content = stripBracketedClean(message.content);
            }
            chat.messages.push(message);

            scrollToBottomOfChat();

            if (message.role === 'assistant') {
                if(message.audioData) {
                    setCurrentAudioData(message.audioData);
                }

                if(message.emotionalReaction) {
                    setEmotion(message.emotionalReaction);
                }
            }

            return chat.messages.length - 1;
        }

        async function translateChatMessage(index) {
            if (chat.messages[index].translationAllowed) {
                if (chat.messages[index].translationActive) {
                    chat.messages[index].translationActive = false;
                } else {
                    chat.messages[index].translationActive = true;

                    if (!chat.messages[index].contentTranslated) {
                        chat.messages[index].contentTranslated = t('chatbot.chat.message.translating');
                        translateTextIntoNativeLanguage(index);
                    }
                }
            }
        }

        function translateTextIntoNativeLanguage(index) {
            const data = new FormData();
            data.append('action', 'translate_text');
            data.append('message', chat.messages[index].content);
            data.append('language', state.value.language.name);
            data.append('mode', state.value.mode);

            const signal = activeRequests.add(`translate-${index}`);

            fetch(__yuki.backend.url, {
                method: "POST",
                body: data,
                signal: signal
            })
                .then((response) => {
                    // Check if request was aborted
                    if (signal.aborted) {
                        throw new Error('AbortError');
                    }
                    return response.json();
                }).then((data) => {
                debug('translate text response', data);

                // Check if request was aborted before processing
                if (signal.aborted) {
                    throw new Error('AbortError');
                }

                if(data.translation) {
                    chat.messages[index].contentTranslated = data.translation;
                } else {
                    console.error('no translation text answer from backend');
                    chat.messages[index].contentTranslated = t('chatbot.chat.message.transcribing.error');
                }
            }).catch((error) => {
                if (error.name !== 'AbortError') {
                    console.error(error);
                }
            });
        }
        function addTextMessage() {
            chat.generating = true;

            const textMessageIndex = appendChatMessage({
                role: "user",
                content: chat.textInput,
                contentTranslated: "",
                translationAllowed: false,
                translationActive: false,
                isTranscribed: false,
                error: false,
                serverId: null,
                audioData: null,
                emotionalReaction: null
            });

            const data = new FormData();
            data.append('action', 'add_text_message');
            data.append('language', state.value.language.name);
            data.append('mode', state.value.mode);
            data.append('message', chat.textInput);

            chat.textInput = '';

            const signal = activeRequests.add('add-text-message');

            fetch(__yuki.backend.url, {
                method: "POST",
                body: data,
                signal: signal
            })
                .then((response) => {
                    // Check if request was aborted
                    if (signal.aborted) {
                        throw new Error('AbortError');
                    }
                    return response.json();
                }).then((data) => {
                debug('add text message', data);

                // Check if request was aborted before processing
                if (signal.aborted) {
                    throw new Error('AbortError');
                }

                if(data.success) {
                    if(data.input.message_id) {
                        chat.messages[textMessageIndex].serverId = data.message_id;
                    }

                    if(data.output.message) {
                        appendChatMessage({
                            role: "assistant",
                            content: data.output.message,
                            contentTranslated: "",
                            translationAllowed: true,
                            translationActive: false,
                            isTranscribed: false,
                            error: false,
                            serverId: data.output.message_id,
                            audioData: data.output.audio_data,
                            emotionalReaction: data.output.emotional_reaction,
                        });
                    } else {
                        console.error('no answer from assistant');
                        chat.messages[textMessageIndex].error = true;
                    }
                } else {
                    console.error(data.error);
                    chat.messages[textMessageIndex].error = true;
                }

                chat.generating = false;
            }).catch((error) => {
                if (error.name !== 'AbortError') {
                    console.error(error);
                    chat.messages[textMessageIndex].error = true;
                }
                chat.generating = false;
            });
        }

        function resetCurrentAudioData() {
            chat.currentAudioData = null;
        }

        function setCurrentAudioData(audioData) {
            chat.currentAudioData = audioData;
        }

        function setEmotion(emotion) {
            state.value.emotion = emotion;
        }

        function resetEmotion() {
            state.value.emotion = Emotions.NEUTRAL;
        }

        function changeColorScheme(scheme) {
            //TODO: use state.value.colorSchemeOverride, instead of manipulating state.value.view.colorScheme, access the activeColorScheme via a computed property that either returns the color scheme of the view or the colorSchemeOverride
            state.value.view.colorScheme = scheme;

            // Close the color picker
            chat.showColorPicker = false;
        }

        return {
            state,
            chat,
            chatbotClasses,
            links,
            tapToStart,
            acceptDisclaimer,
            changeNativeLanguage,
            startTutorial,
            next,
            letsStart,
            selectModeSmalltalk,
            selectModeSupport,
            availableLanguages,
            availableModes,
            availableViews,
            availableColorSchemes,
            checkHasReadDisclaimer,
            selectLanguage,
            goToModeSelect,
            translateChatMessage: translateChatMessage,
            addTextMessage,
            startRecording,
            stopRecording,
            changeToView,
            changeToMode,
            changeColorScheme,
            t,
            avatarRef,
            viewClass
        };

    },

    mounted() {
        if (__yuki.env.requestedView && Views.hasOwnProperty(__yuki.env.requestedView)) {
            this.state.loading = false;
            this.changeToView(Views[__yuki.env.requestedView]);
        } else {
            setTimeout(() => {
                this.state.loading = false;
                this.changeToView(Views.LOGOS);

                setTimeout(() => {
                    this.changeToView(Views.SPLASHSCREEN);
                }, 4000);
            }, 3000);
        }
    }
});

const avatarComponent = {
    props: ['emotion', 'colorScheme', 'size', 'audioData'],

    template: '#avatar',

    setup(props, { expose }) {
        const emotion = toRef(props, 'emotion');
        const colorScheme = toRef(props, 'colorScheme');
        const size = toRef(props, 'size');
        const audioData = toRef(props, 'audioData');
        const audioVisualizerCanvas = useTemplateRef('audio-visualizer-canvas');
        const activeAudioContext = ref(null);
        const activeSource = ref(null);

        function cleanupAudio() {
            // Stop any playing audio and clean up audio context
            if (activeSource.value) {
                activeSource.value.stop();
                activeSource.value = null;
            }
            if (activeAudioContext.value) {
                activeAudioContext.value.close();
                activeAudioContext.value = null;
            }
        }

        // Expose cleanupAudio method to parent
        expose({ cleanupAudio });

        // Audio visualization states
        const AudioVisualizationState = {
            STANDBY: 'standby',
            LOADING: 'loading',
            LIVE: 'live'
        };

        // Create a watcher for audioData prop
        watch(() => props.audioData, (newAudioData) => {
            handleAudioVisualization(newAudioData);
        });

        function handleAudioVisualization(base64Audio) {
            cleanupAudio();

            if (base64Audio === null) {
                // Standby state - minimal constant visualization
                visualizeAudio(AudioVisualizationState.STANDBY);
            } else if (base64Audio === 'loading') {
                // Loading/waiting state - smooth pulsing visualization
                visualizeAudio(AudioVisualizationState.LOADING);
            } else {
                // Live audio state - actual audio visualization
                visualizeAudio(AudioVisualizationState.LIVE, base64Audio);
            }
        }

        async function visualizeAudio(state = AudioVisualizationState.STANDBY, base64Audio = null) {
            const canvas = audioVisualizerCanvas.value;
            if (!canvas) return;

            const ctx = canvas.getContext('2d');
            if (!ctx) return;

            // Handle high-DPI displays
            const dpr = window.devicePixelRatio || 1;
            const rect = canvas.getBoundingClientRect();

            // Ensure we have valid dimensions
            if (rect.width === 0 || rect.height === 0) return;

            // Set display size (css pixels) //outcommented since we want the canvas to scale responsively
            //canvas.style.width = rect.width + 'px';
            //canvas.style.height = rect.height + 'px';

            // Set actual size in memory (scaled to account for extra pixel density)
            canvas.width = Math.floor(rect.width * dpr);
            canvas.height = Math.floor(rect.height * dpr);

            // Scale context to ensure correct drawing operations
            ctx.scale(dpr, dpr);

            const centerX = rect.width / 2;
            const centerY = rect.height / 2;
            const radius = Math.min(rect.width, rect.height) * 0.3;
            const barCount = 23;
            const barWidth = Math.max(4, radius * 0.08);

            let dataArray;
            let analyser;
            let isLiveAudio = false;
            let animationFrameId;

            // Initialize dataArray based on state
            if (state === AudioVisualizationState.LIVE && base64Audio) {
                try {
                    // Request audio permissions first
                    await navigator.mediaDevices.getUserMedia({audio: true});

                    // Set up audio context and analyser for live audio
                    activeAudioContext.value = new (window.AudioContext || window.webkitAudioContext)();

                    analyser = activeAudioContext.value.createAnalyser();
                    analyser.fftSize = 256;
                    analyser.smoothingTimeConstant = 0.8;

                    // Convert base64 directly to array buffer
                    const binaryString = atob(base64Audio);
                    const bytes = new Uint8Array(binaryString.length);
                    for (let i = 0; i < binaryString.length; i++) {
                        bytes[i] = binaryString.charCodeAt(i);
                    }

                    const audioBuffer = await activeAudioContext.value.decodeAudioData(bytes.buffer);

                    // Create audio source and connect to analyser
                    activeSource.value = activeAudioContext.value.createBufferSource();
                    activeSource.value.buffer = audioBuffer;
                    activeSource.value.connect(analyser);
                    analyser.connect(activeAudioContext.value.destination);

                    // Start playing the audio
                    activeSource.value.start(0);
                    isLiveAudio = true;

                    dataArray = new Uint8Array(analyser.frequencyBinCount);
                } catch (error) {
                    console.error('Error in audio visualization:', error);
                    return;
                }
            } else {
                // For non-live states, create a static data array
                dataArray = new Uint8Array(256);
            }

            // Calculate bar angles
            const angleIncrement = Math.PI * 2 / barCount;
            const angles = Array.from(
                {length: barCount},
                (_, i) => i * angleIncrement
            );

            // Shuffle angles for live audio
            if (isLiveAudio) {
                for (let i = angles.length - 1; i > 0; i--) {
                    const j = Math.floor(Math.random() * (i + 1));
                    [angles[i], angles[j]] = [angles[j], angles[i]];
                }
            }

            let time = 0;
            const loadingSpeed = 0.05; // Speed of the loading animation
            const standbyAmplitude = 0.035; // Minimal amplitude for standby state

            // Add parameters for loading animation
            const loadingConfig = {
                baseAmplitude: 0.8,    // Reduced for smoother movement
                noiseAmplitude: 0.3,   // Significantly reduced for less shakiness
                waveSpeed: 0.03,       // Slowed down for smoother transitions
                noiseSpeed: 0.05,      // Slowed down noise for less jitter
                phases: Array(barCount).fill(0).map(() => Math.random() * Math.PI * 2),
                noiseOffsets: Array(barCount).fill(0).map(() => Math.random() * 1000),
                // Simulate frequency bands like in real audio, but smoother
                frequencyWeights: Array(barCount).fill(0).map((_, i) => {
                    // Create smoother transitions between frequencies
                    const normalizedIndex = i / barCount;
                    return 0.4 + Math.pow(1 - normalizedIndex, 1.5) * 0.5;
                })
            };

            function draw() {
                if (!canvas.isConnected) {
                    cancelAnimationFrame(animationFrameId);
                    return;
                }

                ctx.clearRect(0, 0, canvas.width, canvas.height);

                if (state === AudioVisualizationState.LIVE && isLiveAudio) {
                    // Live audio visualization
                    analyser.getByteFrequencyData(dataArray);
                } else if (state === AudioVisualizationState.LOADING) {
                    // Loading state - create dynamic wave-like effect with randomization
                    time += loadingConfig.waveSpeed;

                    // Update each bar individually
                    for (let i = 0; i < dataArray.length; i++) {
                        const barIndex = i % barCount;

                        // Simulate frequency characteristics
                        const frequencyWeight = loadingConfig.frequencyWeights[barIndex];

                        // Smoother base wave with fewer harmonics
                        const baseWave = (
                            Math.sin(time * frequencyWeight + loadingConfig.phases[barIndex]) * 0.7 +
                            Math.sin(time * frequencyWeight * 1.5 + loadingConfig.phases[barIndex]) * 0.2 +
                            Math.sin(time * frequencyWeight * 2.0 + loadingConfig.phases[barIndex]) * 0.1
                        );

                        // Smoother noise effect
                        const noiseTime = time * loadingConfig.noiseSpeed + loadingConfig.noiseOffsets[barIndex];
                        const noise = (
                            Math.sin(noiseTime) *
                            Math.sin(noiseTime * 0.5)
                        ) * (0.3 + frequencyWeight * 0.4); // Reduced noise impact

                        // Combine waves with smoother transitions
                        let amplitude = baseWave * loadingConfig.baseAmplitude + noise * loadingConfig.noiseAmplitude;

                        // Smoother exponential scaling
                        amplitude = Math.sign(amplitude) * Math.pow(Math.abs(amplitude), 0.9);

                        // Normalize to 0-1 range with enhanced range
                        amplitude = (amplitude + 1) / 2;

                        // Smoother frequency weighting
                        amplitude = amplitude * (0.4 + frequencyWeight * 0.6);

                        // Convert to byte range (0-255)
                        dataArray[i] = Math.floor(amplitude * 255);
                    }
                } else {
                    // Standby state - minimal constant visualization
                    dataArray.fill(Math.floor(standbyAmplitude * 255));
                }

                for (let i = 0; i < barCount; i++) {
                    const angle = angles[i]; // Einzelner Winkel für die Bar

                    // Empfindlichkeit für tiefere Frequenzen anpassen
                    let barHeight = dataArray[i] || 1;
                    barHeight = Math.pow(barHeight, 1.1); // Verstärkung der niedrigen Frequenzen

                    // Berechnung für Bewegung nach innen und außen
                    const innerRadius = radius - barHeight / 20; // Balken nach innen
                    const outerRadius = radius + barHeight / 10; // Balken nach außen

                    // Start- und Endpunkte der Balken berechnen
                    const x1 = centerX + Math.cos(angle) * innerRadius;
                    const y1 = centerY + Math.sin(angle) * innerRadius;

                    const x2 = centerX + Math.cos(angle) * outerRadius;
                    const y2 = centerY + Math.sin(angle) * outerRadius;

                    ctx.beginPath();
                    ctx.moveTo(x1, y1);
                    ctx.lineTo(x2, y2);
                    ctx.lineWidth = barWidth;
                    ctx.lineCap = 'round';
                    ctx.strokeStyle = getComputedStyle(canvas).getPropertyValue('--yuki-chatbot-avatar-color') || '#0944ba';
                    ctx.stroke();
                }

                animationFrameId = requestAnimationFrame(draw);
            }

            draw();

            return () => {
                if (animationFrameId) {
                    cancelAnimationFrame(animationFrameId);
                }
            };
        }

        onMounted(() => {
            handleAudioVisualization(props.audioData);
        });

        onUnmounted(() => {
            cleanupAudio();
        });

        return {
            emotion,
            colorScheme,
            size,
            audioData
        };
    }
}

const avatarEyesComponent = {
    props: ['emotion', 'colorScheme'],

    template: '#avatar-eyes',

    setup(props) {
        const emotion = toRef(props, 'emotion');
        const colorScheme = toRef(props, 'colorScheme');
        const avatarEyesSceneContainer = useTemplateRef('avatar-eyes-scene-container');
        const renderer = ref(null);
        const scene = ref(null);
        const camera = ref(null);
        const morphableMeshes = ref({});

        const data = reactive({
            isLoading: true,
            avatarColor: '#0944ba', // Default color until mounted
            currentEmotion: 'neutral',
            targetEmotion: 'neutral',
            randomModifier: 1.0,
            modifierChangeTimeout: null,
            blinkOffTimeout: null,
            blinkResetTimeout: null,
            randomBlinkTimeout: null,
            blinkState: 0, // 0: not blinking; 1: closing; 2: opening
            emotions: {
                neutral: {
                    happy: 0.0,
                    worried: 0.0,
                    unsure: 0.0,
                },
                happy: {
                    happy: 1.0,
                    worried: 0.0,
                    unsure: 0.0,
                },
                worried: {
                    happy: 0.0,
                    worried: 1.0,
                    unsure: 0.0,
                },
                unsure: {
                    happy: 0.0,
                    worried: 0.0,
                    unsure: 1.0,
                },
            },
        });

        function determineAvatarColor() {
            return getComputedStyle(avatarEyesSceneContainer.value).getPropertyValue('--yuki-chatbot-avatar-color') || '#0944ba';
        }

        function updateMeshColors(color) {
            if (!scene.value) return;
            
            scene.value.traverse((child) => {
                if (child.isMesh && child.material) {
                    child.material.color.set(color);
                }
            });
        }

        watch(() => props.emotion, (newEmotion) => {
            if (newEmotion) {
                setEmotion(newEmotion);
            }
        }, { immediate: true });

        watch(() => props.colorScheme, () => {
            nextTick(() => {
                if (avatarEyesSceneContainer.value) {
                    const newColor = determineAvatarColor();
                    data.avatarColor = newColor;
                    updateMeshColors(newColor);
                }
            })
        });

        function initThree() {
            if (!avatarEyesSceneContainer.value) return;

            //respect device pixel ratio (prevent blurry canvas)
            const dpr = window.devicePixelRatio || 1;
            const width = avatarEyesSceneContainer.value.clientWidth * dpr;
            const height = avatarEyesSceneContainer.value.clientHeight * dpr;

            scene.value = markRaw(new THREE.Scene());
            scene.value.background = null; // Set to null for transparency
            camera.value = markRaw(new THREE.PerspectiveCamera(60, width / height, 0.1, 1000));
            renderer.value = markRaw(new THREE.WebGLRenderer({ 
                antialias: true,
                alpha: true // Enable alpha channel for transparency
            }));
            renderer.value.setClearColor(0x000000, 0); // Set clear color with 0 alpha
            renderer.value.setSize(width, height);
            avatarEyesSceneContainer.value.appendChild(renderer.value.domElement);
        }

        function loadModel() {
            const loader = markRaw(new GLTFLoader());
            loader.load(
                __yuki.constants.avatarEyesGlb,
                (gltf) => {
                    const model = markRaw(gltf.scene);
                    scene.value.add(model);

                    // Update matrices before measuring
                    model.updateWorldMatrix(true, true);

                    // Compute bounding box
                    const box = markRaw(new THREE.Box3().setFromObject(model));
                    const center = markRaw(box.getCenter(new THREE.Vector3()));

                    // Shift model so its center is at (0,0,0)
                    model.position.x -= center.x;
                    model.position.y -= center.y;
                    model.position.z -= center.z;

                    // Make sure the camera is far enough away:
                    camera.value.position.set(0, 0, 0.7);
                    camera.value.lookAt(0, 0, 0);

                    // We'll store info about each mesh that has morph targets
                    morphableMeshes.value = {};
                    gltf.scene.traverse((child) => {
                        if (child.isMesh) {
                            child.material = markRaw(new THREE.MeshBasicMaterial({ color: data.avatarColor }));
                        }
                        if (child.isMesh && child.morphTargetDictionary && child.morphTargetInfluences) {
                            const meshName = child.name || `MorphMesh-${Math.random()}`;
                            morphableMeshes.value[meshName] = markRaw({
                                mesh: child,
                                dictionary: child.morphTargetDictionary,
                                influences: child.morphTargetInfluences,
                            });
                        }
                    });
                    scheduleRandomBlink();
                    adjustRandomModifier();
                    data.isLoading = false;
                },
                undefined,
                (error) => {
                    console.error('Error loading model:', error);
                    data.isLoading = false;
                },
            );
        }

        function animate() {
            requestAnimationFrame(animate);
            updateBlendshapes();
            adjustCamera();

            if (renderer.value && scene.value && camera.value) {
                renderer.value.render(scene.value, camera.value);
            }
        }

        function updateBlendshapes() {
            if (data.isLoading) {
                return;
            }
            let currentHappy = getBlendShape('EyeAvatar', 'Happy') || 0;
            let currentWorried = getBlendShape('EyeAvatar', 'Worried') || 0;
            let currentUnsure = getBlendShape('EyeAvatar', 'Unsure') || 0;
            let currentBlink = getBlendShape('EyeAvatar', 'Blink') || 0;

            let targetWeights = data.emotions[data.targetEmotion] || data.emotions.neutral;
            let targetHappy = 0;
            let targetWorried = 0;
            let targetUnsure = 0;
            let targetBlink = 0;
            let blinkAlpha = 0.2;

            if (data.blinkState === 1) {
                targetHappy = 0;
                targetWorried = 0;
                targetUnsure = 0;
                targetBlink = 1;
                blinkAlpha = 0.3;
            } else {
                targetHappy = targetWeights.happy;
                targetWorried = targetWeights.worried;
                targetUnsure = targetWeights.unsure;
                targetBlink = 0;
                blinkAlpha = 0.2;
            }

            //update current emotion when animation is finished
            let totalDiff =
                Math.abs(targetHappy * data.randomModifier - currentHappy) +
                Math.abs(targetWorried * data.randomModifier - currentWorried) +
                Math.abs(targetUnsure * data.randomModifier - currentUnsure) +
                Math.abs(targetBlink - currentBlink);
            if (totalDiff < 0.01) {
                data.currentEmotion = data.targetEmotion;
                return;
            }

            setBlendShape('EyeAvatar', 'Happy', lerp(currentHappy, targetHappy * data.randomModifier, 0.15));
            setBlendShape('EyeAvatar', 'Worried', lerp(currentWorried, targetWorried * data.randomModifier, 0.15));
            setBlendShape('EyeAvatar', 'Unsure', lerp(currentUnsure, targetUnsure * data.randomModifier, 0.15));
            setBlendShape('EyeAvatar', 'Blink', lerp(currentBlink, targetBlink, blinkAlpha));
        }
        function adjustCamera() {
            let camX = 0
            let camY = 0
            if (data.targetEmotion == 'happy') {
              camX = lerp(camera.value.position.x, -0.05 * data.randomModifier, 0.1)
              camY = lerp(camera.value.position.y, -0.05 * data.randomModifier, 0.1)
            } else if (data.targetEmotion == 'worried') {
              camX = lerp(camera.value.position.x, 0.05 * data.randomModifier, 0.1)
              camY = lerp(camera.value.position.y, 0.05 * data.randomModifier, 0.1)
            } else {
              camX = lerp(camera.value.position.x, 0, 0.1)
              camY = lerp(camera.value.position.y, 0, 0.1)
            }
            camera.value.position.set(camX, camY - 0.005, 0.7
            )
          }
        function getBlendShape(meshName, shapeName) {
            const meshEntry = morphableMeshes.value[meshName];
            if (!meshEntry) return undefined;
            const index = meshEntry.dictionary[shapeName];
            if (index === undefined) return undefined;
            return meshEntry.influences[index];
        }

        function setBlendShape(meshName, shapeName, value) {
            const meshEntry = morphableMeshes.value[meshName];
            if (!meshEntry) return;
            const index = meshEntry.dictionary[shapeName];
            if (index === undefined) return;
            meshEntry.influences[index] = value;
        }

        function setEmotion(emotionName) {
            //Take emotions from backend constants, fallback to default emotions
            const validEmotions = Object.values(__yuki.constants.emotions) || ['neutral', 'happy', 'worried', 'unsure'];

            if (!validEmotions.includes(emotionName)) {
                console.error(
                    `Invalid emotion name: "${emotionName}". Must be one of: ${validEmotions.join(', ')}`,
                );
                return;
            }

            if (emotionName !== data.currentEmotion) {
                data.targetEmotion = emotionName;
            }
        }

        function adjustRandomModifier() {
            const minDelaySeconds = 2;
            const maxDelaySeconds = 7;
            const randomDelay =
                minDelaySeconds * 1000 + Math.random() * ((maxDelaySeconds - minDelaySeconds) * 1000);
            data.randomModifier = 1 - Math.random() * 0.5;
            data.modifierChangeTimeout = setTimeout(() => {
                adjustRandomModifier();
            }, randomDelay);
        }

        function blinkOn() {
            data.blinkState = 1;
            data.blinkOffTimeout = setTimeout(() => {
                blinkOff();
            }, 100);
        }

        function blinkOff() {
            data.blinkState = 2;
            data.blinkResetTimeout = setTimeout(() => {
                data.blinkState = 0;
            });
            scheduleRandomBlink();
        }

        function scheduleRandomBlink() {
            const minDelaySeconds = 2;
            const maxDelaySeconds = 10;
            const randomDelay =
                minDelaySeconds * 1000 + Math.random() * ((maxDelaySeconds - minDelaySeconds) * 1000);
            data.randomBlinkTimeout = setTimeout(() => {
                blinkOn();
            }, randomDelay);
        }

        onMounted(() => {
            // Update avatar color after mount when we have access to the DOM
            data.avatarColor = determineAvatarColor();
            initThree();
            loadModel();
            animate();
        });

        onUnmounted(() => {
            // Clean up timeouts
            clearTimeout(data.modifierChangeTimeout);
            clearTimeout(data.blinkOffTimeout);
            clearTimeout(data.blinkResetTimeout);
            clearTimeout(data.randomBlinkTimeout);

            if(renderer.value) {
                renderer.value.dispose();
                renderer.value = null;
            }
        });

        return {
            emotion,
            colorScheme,
            data
        };
    }
};

app.component('avatar', avatarComponent);
app.component('avatar-eyes', avatarEyesComponent);

app.use(i18n);
app.mount('#yuki')